import { Button } from "@/components/ui/button";
import { Home, ArrowRight, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

interface Props { onStart: () => void; }

const LEARNING_OBJECTIVES = [
  "Choose a home that fits your budget and lifestyle",
  "Calculate down payment options and their tradeoffs",
  "Understand closing costs and what they include",
  "Compare fixed vs. adjustable-rate mortgages",
  "See how a home purchase impacts your total financial picture",
];

export default function HomeUnlockScreen({ onStart }: Props) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="glass-card glow-border max-w-md w-full p-8 text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto">
          <Home className="w-8 h-8 text-accent" />
        </div>
        <h1 className="font-heading text-xl font-bold text-foreground">Real Estate (Homeownership): Module</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          You completed the Homeownership course. Now it's time to walk through buying a house.
          You'll choose a home, decide on your down payment, review closing costs, select a mortgage option,
          and see how the purchase affects your finances.
        </p>

        {/* What You'll Learn */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-accent/20 bg-accent/5 p-4 space-y-2.5 text-left"
        >
          <p className="text-xs font-heading font-semibold text-accent uppercase tracking-wider">What You'll Learn</p>
          {LEARNING_OBJECTIVES.map((obj, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
              <span className="text-xs text-foreground/80">{obj}</span>
            </div>
          ))}
        </motion.div>

        {/* How It Works */}
        <div className="rounded-xl border border-border/60 bg-card/60 p-4 text-left">
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">How it works:</strong> You'll browse homes, set a down payment, review mortgage options, and see the full cost breakdown. Each step teaches a real concept about homeownership.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button onClick={onStart} className="w-full gap-2">Start Module <ArrowRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => navigate("/")} className="w-full">Back to Dashboard</Button>
        </div>
      </div>
    </div>
  );
}
